import React, { ComponentType as ReactComponentType, LazyExoticComponent, ReactNode, Suspense } from "react";

/**
 * Type for a React component, which can be either a regular component or a lazy-loaded component.
 */
type ComponentType<P = Record<string, unknown>> =
    | ReactComponentType<P>
    | LazyExoticComponent<ReactComponentType<P>>;

/**
 * Conditional function type that takes props and returns boolean.
 */
type ConditionalFunction<P = Record<string, unknown>> = (props: P) => boolean;

/**
 * Entry in the component registry.
 */
interface ComponentEntry<P = Record<string, unknown>> {
    component: ComponentType<P>;
    isLazy: boolean; // true if it's a lazy component
    loadingFallback?: ReactNode; // fallback for lazy components
    shouldRender?: ConditionalFunction<P>; // optional shouldRender function
}

/**
 * Singleton base class for registries.
 * It is needed because the generic types T and P from BaseComponentRegistry cannot be used in static properties
 */
abstract class BaseSingleton {
    /**
     * Singleton instance of the registry.
     */
    protected static instance: BaseSingleton;

    /**
     * Gets the singleton instance of the registry.
     */
    public static getInstance<T extends BaseSingleton>(this: new () => T): T {
        if (!(this as any).instance)
            (this as any).instance = new this();

        return (this as any).instance;
    }
}

/**
 * Base registry for React components.
 */
abstract class BaseComponentRegistry<T extends number, P extends Record<string, any> & { componentId?: T } = { componentId?: T }> extends BaseSingleton {
    /**
     * Registry of components, keyed by their unique IDs.
     * Each entry contains the component, module name, lazy status, loading fallback, cleanup functions, and an optional shouldRender function.
     */
    protected registry: { [key in T]?: ComponentEntry<P> } = {};

    /**
     * Set of module names that have/had components registered in this registry.
     * Used to track which modules affects the registry and trigger re-renders.
     */
    protected moduleNamesHistory: Set<string> = new Set();

    /**
     * Creates a shallow copy to trigger re-renders when using the useComponentRegistry hook.
     * @returns A new instance of BaseComponentRegistry with the same underlying registry.
     */
    public shallowCopy<R extends BaseComponentRegistry<T, P>>(): R {
        const newRegistry = new (this.constructor as new () => R)();
        newRegistry.registry = this.registry;
        newRegistry.moduleNamesHistory = this.moduleNamesHistory;

        return newRegistry;
    }

    /**
    * Register a component that takes props.
    * @param componentId The unique identifier for the component.
    * @param component The component function.
    * @param moduleName The name of the module the component belongs to.
    * @param shouldRender Optional function to determine if the component should render.
    */
    public registerComponent(
        componentId: T,
        component: ReactComponentType<P>,
        moduleName: string,
        shouldRender?: ConditionalFunction<P>
    ): void {
        // Validate at registration time
        if (typeof component !== "function") {
            throw new Error(`Component ${componentId} must be a function when using registerComponent`);
        }

        this.registry[componentId] = {
            component,
            isLazy: false,
            shouldRender
        };
        this.moduleNamesHistory.add(moduleName);
    }

    /**
     * Register a lazy component with optional fallback and shouldRender.
     * @param componentId The unique identifier for the component.
     * @param component The lazy component.
     * @param moduleName The name of the module the component belongs to.
     * @param shouldRender Optional function to determine if the component should render.
     * @param loadingFallback Optional fallback content to display while loading.
     */
    public registerLazyComponent(
        componentId: T,
        component: LazyExoticComponent<ReactComponentType<P>>,
        moduleName: string,
        shouldRender?: ConditionalFunction<P>,
        loadingFallback?: ReactNode
    ): void {
        // Validate lazy component
        if (!component || typeof component !== "object") {
            throw new Error(`Component ${componentId} must be a valid lazy component when using registerLazyComponent`);
        }

        this.registry[componentId] = {
            component,
            isLazy: true,
            loadingFallback,
            shouldRender
        };
        this.moduleNamesHistory.add(moduleName);
    }

    /**
     * Unregister a component by its ID.
     * @param componentId The unique identifier for the component.
     */
    public unregisterComponent(componentId: T): void {
        if (componentId in this.registry)
            delete this.registry[componentId];
    }

    /**
     * Unregister multiple components by their IDs.
     * @param componentIds The unique identifiers for the components.
     */
    public unregisterComponents(componentIds: T[]): void {
        for (const id of componentIds) {
            this.unregisterComponent(id);
        }
    }

    /**
     * Render registered components based on props.
     * If a componentId is provided in the props, only that component will be rendered.
     * @param props The props to pass to each component.
     * @returns A ReactNode containing all rendered components.
     */
    public render(props: P): ReactNode | null {
        if (props.componentId)
            return this.renderComponent(props.componentId, props);

        return Object.keys(this.registry)
            .map(key => parseInt(key) as T)
            .sort((a, b) => a - b)
            .map(componentId => React.createElement(
                React.Fragment,
                { key: componentId },
                this.renderComponent(componentId, props)
            ));
    }

    /**
     * Get the names of all modules that have registered components.
     */
    public getModuleNamesHistory(): Set<string> {
        return new Set(this.moduleNamesHistory);
    }

    /**
     * Check if a component should render based on its shouldRender function.
     * @param componentId The unique identifier for the component.
     * @param props The props to pass to the shouldRender function.
     * @returns True if the component should render, false otherwise.
     */
    private shouldRender(componentId: T, props: P): boolean {
        const entry = this.registry[componentId];
        if (!entry) return false;

        // If no shouldRender function, always render
        if (!entry.shouldRender) return true;

        // Execute shouldRender function with props
        try {
            return entry.shouldRender(props);
        }
        catch (error) {
            console.error(`Error in shouldRender function for component ${componentId}:`, error);
            return false; // Default to not rendering on error
        }
    }

    /**
     * Render a component with props (handles regular, lazy, and static components)
     * @param componentId The unique identifier for the component.
     * @param props The props to pass to the component.
     * @returns The rendered component or null if not found or should not render.
     */
    private renderComponent(componentId: T, props: P): ReactNode | null {
        const entry = this.registry[componentId];
        if (!entry) {
            console.warn(`Component with id ${componentId} not found in registry`);
            return null;
        }

        // Check shouldRender before rendering
        if (!this.shouldRender(componentId, props)) {
            return null;
        }

        // Handle lazy components with Suspense
        if (entry.isLazy) {
            const LazyComponent = entry.component as LazyExoticComponent<ReactComponentType<P>>;
            const fallback = entry.loadingFallback || React.createElement("div", null, "Loading...");

            return React.createElement(
                Suspense,
                { fallback },
                React.createElement(LazyComponent, props as any)
            );
        }

        // Handle regular components - no type check needed since validation occurs at registration
        const Component = entry.component as ReactComponentType<P>;
        return React.createElement(Component, props as any);
    }
}

export { BaseComponentRegistry, ComponentType, ConditionalFunction };
